const _ = require('../../lodash');
const getConstants = require('../../utils/getConstants');

const constants = getConstants();
const { issueStates } = constants;

const ISSUE_RAISED = 'ISSUE_RAISED';
const ISSUE_DEFINE_UPDATED = 'ISSUE_DEFINE_UPDATED';
const ISSUE_ASSIGNEDTO_SPECIFIC_TEAMS = 'ISSUE_ASSIGNEDTO_SPECIFIC_TEAMS';
const ISSUE_ASSIGNEDTO_SPECIFIC_POSITIONS = 'ISSUE_ASSIGNEDTO_SPECIFIC_POSITIONS';
const ISSUE_ASSIGNEDTO_SPECIFIC_STAFF = 'ISSUE_ASSIGNEDTO_SPECIFIC_STAFF';
const ISSUE_UNASSIGNED_FROM_EVERYONE = 'ISSUE_UNASSIGNED_FROM_EVERYONE';
const ISSUE_TAGGED = 'ISSUE_TAGGED';
const ISSUE_UNTAGGED = 'ISSUE_UNTAGGED';
const ISSUE_CLOSED = 'ISSUE_CLOSED';
const ISSUE_REOPENED = 'ISSUE_REOPENED';
const ISSUE_COMMENT_ADDED = 'ISSUE_COMMENT_ADDED';
const ISSUE_COMMENT_DELETED = 'ISSUE_COMMENT_DELETED';

const VISIBLE_MESSAGE_TYPES = [
   ISSUE_RAISED,
   ISSUE_DEFINE_UPDATED,
   ISSUE_ASSIGNEDTO_SPECIFIC_TEAMS,
   ISSUE_ASSIGNEDTO_SPECIFIC_POSITIONS,
   ISSUE_ASSIGNEDTO_SPECIFIC_STAFF,
   ISSUE_UNASSIGNED_FROM_EVERYONE,
   ISSUE_TAGGED,
   ISSUE_UNTAGGED,
   ISSUE_CLOSED,
   ISSUE_REOPENED,
   ISSUE_COMMENT_ADDED,
   ISSUE_COMMENT_DELETED
];

const UNKNOWN_ACTOR = 'Someone';

const compact = (values) => _.filter(values, (value) => value != null && value !== '');

const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural || `${singular}s`}`;

const joinCaptions = (captions = []) => {
   const validCaptions = compact(captions);

   if (validCaptions.length === 0) {
      return null;
   }

   if (validCaptions.length === 1) {
      return validCaptions[0];
   }

   if (validCaptions.length === 2) {
      return `${validCaptions[0]} and ${validCaptions[1]}`;
   }

   return `${validCaptions.slice(0, -1).join(', ')}, and ${validCaptions[validCaptions.length - 1]}`;
};

const describeChangedFields = ({ before = {}, after = {}, priorityCaption }) => {
   const fields = [];

   if (before.title !== after.title) {
      fields.push('title');
   }

   if (before.description !== after.description) {
      fields.push('description');
   }

   if (before.noPriority !== after.noPriority) {
      fields.push(priorityCaption ? `priority to ${priorityCaption}` : 'priority');
   }

   return joinCaptions(fields);
};

const describeClosed = ({ actorCaption, payload }) => {
   const state = Number(payload?.state || 0);
   const isResolved = (state & issueStates.RESOLVED) === issueStates.RESOLVED;
   const reason = payload?.closedReason || payload?.reason || null;

   if (isResolved) {
      return {
         kind: 'lifecycle',
         tone: 'success',
         title: 'Closed as resolved',
         detail: reason
            ? `${actorCaption} closed this issue as resolved. Reason: ${reason}`
            : `${actorCaption} closed this issue as resolved`
      };
   }

   return {
      kind: 'lifecycle',
      tone: 'success',
      title: 'Closed',
      detail: reason ? `${actorCaption} closed this issue. Reason: ${reason}` : `${actorCaption} closed this issue`
   };
};

const buildAssignmentCaption = ({ payload = {}, captions = {} }) => {
   const teamCaptions = _.map(payload.nosTeam || [], (no) => captions.teams?.[no] || `Team #${no}`);
   const positionCaptions = _.map(payload.nosPosition || [], (no) => captions.positions?.[no] || `Position #${no}`);
   const staffCaptions = _.map(payload.nosStaff || [], (no) => captions.staff?.[no] || `Staff #${no}`);

   return joinCaptions([...teamCaptions, ...positionCaptions, ...staffCaptions]);
};

const describeIssueTimelineEvent = ({ messageType, payload = {}, actorCaption = UNKNOWN_ACTOR, captions = {} }) => {
   switch (messageType) {
      case ISSUE_RAISED:
         return {
            kind: 'created',
            tone: 'created',
            title: 'Issue raised',
            detail: `${actorCaption} raised this issue`
         };

      case ISSUE_DEFINE_UPDATED: {
         const changedFields = describeChangedFields({ before: payload.before, after: payload.after, priorityCaption: captions.priority });
         return {
            kind: 'details',
            tone: 'updated',
            title: 'Details edited',
            detail: changedFields ? `${actorCaption} edited the issue ${changedFields}` : `${actorCaption} edited the issue details`
         };
      }

      case ISSUE_ASSIGNEDTO_SPECIFIC_TEAMS:
      case ISSUE_ASSIGNEDTO_SPECIFIC_POSITIONS:
      case ISSUE_ASSIGNEDTO_SPECIFIC_STAFF: {
         const targetCaption = buildAssignmentCaption({ payload, captions });
         return {
            kind: 'assignment',
            tone: 'assignment',
            title: 'Assignment changed',
            detail: targetCaption
               ? `${actorCaption} assigned this issue to ${targetCaption}`
               : `${actorCaption} changed the issue assignment`
         };
      }

      case ISSUE_UNASSIGNED_FROM_EVERYONE:
         return {
            kind: 'assignment',
            tone: 'assignment',
            title: 'Unassigned',
            detail: `${actorCaption} unassigned this issue`
         };

      case ISSUE_TAGGED: {
         const tagCaption = captions.tag || payload.name || (payload.noTag != null ? `Tag #${payload.noTag}` : 'a tag');
         return {
            kind: 'tag',
            tone: 'tag',
            title: 'Tag added',
            detail: `${actorCaption} added tag “${tagCaption}”`
         };
      }

      case ISSUE_UNTAGGED: {
         const tagCaption = captions.tag || payload.name || (payload.noTag != null ? `Tag #${payload.noTag}` : 'a tag');
         return {
            kind: 'tag',
            tone: 'tag',
            title: 'Tag removed',
            detail: `${actorCaption} removed tag “${tagCaption}”`
         };
      }

      case ISSUE_CLOSED:
         return describeClosed({ actorCaption, payload });

      case ISSUE_REOPENED:
         return {
            kind: 'lifecycle',
            tone: 'state',
            title: 'Reopened',
            detail: `${actorCaption} reopened this issue`
         };

      case ISSUE_COMMENT_ADDED:
         return {
            kind: 'note',
            tone: 'note',
            title: 'Note added',
            detail: `${actorCaption} added a note`
         };

      case ISSUE_COMMENT_DELETED:
         return {
            kind: 'note',
            tone: 'note',
            title: 'Note deleted',
            detail: `${actorCaption} deleted a note`
         };

      default:
         return null;
   }
};

module.exports = {
   VISIBLE_MESSAGE_TYPES,
   describeIssueTimelineEvent,
   joinCaptions,
   pluralize
};
